/**
 * Usage Check Hooks - Migrated to Jotai
 *
 * Checks if a document is used/referenced by other documents
 * Used for validating deletes (e.g., can't delete class if students enrolled)
 */

import { useAtomValue, useSetAtom } from "jotai"
import { useCallback, useEffect } from "react"

import {
  createUsageCheckKey,
  usageCheckResultAtom,
} from "#atoms/usageCheckAtoms"
import Firebase from "#classes/Firebase"
import {
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DatabaseObject } from "#customTypes/types"

export interface CheckUsageQuery {
  docType: DOC_TYPES
  queries: FirebaseQueryConfig["queries"]
}

/**
 * Hook for checking if a document is used by other doc types
 * Results are cached in Jotai atoms
 */
export function useCheckUsage<T extends DatabaseObject>(
  docType: DOC_TYPES,
  id: Uuid | null | undefined,
  queries: CheckUsageQuery[]
) {
  const key = createUsageCheckKey(docType, id)
  const result = useAtomValue(usageCheckResultAtom(key))
  const setResult = useSetAtom(usageCheckResultAtom(key))

  useEffect(() => {
    if (!id) return

    // Fetch usage check
    const checkUsage = async () => {
      console.log("checking usage...")
      const results = await Promise.all(
        queries.map(async ({ queries, docType }) => {
          const docs = await Firebase.getInstance().variableQuery<T>(docType, {
            queries,
            limitCount: 1,
          })

          return docs.length > 0
        })
      )

      const isUsed = results.some(Boolean)
      setResult(isUsed)
    }

    checkUsage()
  }, [id, key, queries, setResult])

  return {
    data: result ?? false,
    isPending: result === null,
    isFetched: result !== null,
    error: null,
  }
}

/**
 * Invalidate (refetch) a usage check
 * With Jotai, just set to null to trigger refetch
 */
export function useInvalidateCheckUsage(
  docType: DOC_TYPES,
  id: Uuid | null | undefined
) {
  const key = createUsageCheckKey(docType, id)
  const setResult = useSetAtom(usageCheckResultAtom(key))

  return useCallback(() => {
    if (!id) return
    setResult(null) // Reset to trigger refetch
  }, [setResult, id])
}
